import * as React from 'react';
import { History } from 'history';
import {
  ISelectedAnalys,
  ISelectedAnalysCodePrivilege,
} from '../../EkonomiskAnalysPage';
import { equals } from 'ramda';
import {
  IEkonomiskAnalysCardSettings,
  IFilledLocalAnalys,
} from '../EkonomiskAnalysMain/EkonomiskAnalysMain';
import { ISelection } from '../../Queries/EkonomiskAnalysInitQuery/EkonomiskAnalysInitQuery';
import { convertTEDUrvalToUIUrval } from '../../../../../../../Components/Urval/Utils/convertTEDUrvalToUIUrval';
import * as UrvalConsts from '../../../../../../../Components/Urval/Consts/UrvalConsts';
import {
  ReportType,
  IColumn,
  ISumData,
} from '../../Queries/EkonomiskAnalysSettingsQuery/EkonomiskAnalysSettingsQuery';
import { IUIUrval } from '../../../../../../../Types/UrvalTypes';
import { IParamValue } from '../../Queries/EkonomiskAnalysChartQuery/EkonomiskAnalysChartQuery';
import { AvenyCollectionMap } from '../../../../../../../Model/ModelContext/ModelContext';
import { ekanalys_ekanalys_columns } from '../../Queries/EkonomiskAnalysSettingsQuery/_QueryTypes/ekanalys';
import { getDateInputType, hasAnyInterval } from '../../Utils/analysUtils';
import { StandardColumnType } from '../SideMenu/Components/PeriodOchKolumner/EditColumn/Components/Queries/EkonomiskAnalysStandardColumnQuery/EkonomiskAnalysStandardColumnQuery';
import {
  defaultBudgetSummationMode,
  defaultSummationMode,
} from '../../Utils/SummationModeUtils';
import {
  AnalysLevelEnum,
  ANALYSKATEGORI_ID_VISA_SAMMANFATTNING,
  MISSING_VALUE_IN_KODDEL_URL,
} from '../../Const/EkonomiskAnalysConsts';
import {
  IUrvalUsedForDrilling,
  getAnalysLevelFromUrlParam,
} from '../../Utils/detaljanalysUtils';
import { Location } from 'history';
import { useLocation } from 'react-router';

interface IProps {
  selectedAnalys: ISelectedAnalys | null;
  availableReports: ISelection[];
  collectionMap: AvenyCollectionMap;
  standardColumns: StandardColumnType[] | undefined;
  onCloseLeftMenu?: () => void;
  children: (args: IChildrenArgs) => React.ReactNode;
  updateImpersonatedCodePrivilege: (
    codePrivilege?: ISelectedAnalysCodePrivilege | undefined
  ) => void;
}

interface IInnerProps extends IProps {
  location: Location<unknown>;
  history: History<{ ekonomiskAnalysSettings: IEkonomiskAnalysCardSettings }>;
}

interface IState {
  originalAnalys: ISelectedAnalys | null;
  originalSettings: IFilledLocalAnalys | null;
  sumsFromUrl: string[];
  analysLevel: AnalysLevelEnum;
  urvalConditionsIUrvalUsedForDrilling: IUrvalUsedForDrilling[];
  drilledFromDetaljanalys: boolean;
}

interface IChildrenArgs {
  analysFromUrl: ISelectedAnalys | null;
  settingsFromUrl: IFilledLocalAnalys | null;
  sumsFromUrl: string[];
  analysLevel: AnalysLevelEnum;
  urvalConditionsIUrvalUsedForDrilling: IUrvalUsedForDrilling[];
  drilledFromDetaljanalys: boolean;
}

interface IParsedAnalys {
  name: string;
  rubrik: string | null;
  kategori: string | null;
  standard_belopp_bas: number | null;
  standard_belopp_antal_decimaler: number | null;
  typ: ReportType;
  columns: ekanalys_ekanalys_columns[];
  sums: ISumData[];
  anvands_for: string[] | null;
}

interface IParsedParams {
  name: string;
  rubrik: string | null;
  kategori: string | null;
  standardBeloppBas: number | null;
  standardBeloppAntalDecimaler: number | null;
  typ: ReportType;
  columns: IColumn[];
  sums: ISumData[];
  grundurvalId: string;
  kodbehorighetId: string;
  detaljUrval: IUIUrval | null;
  anvandarUrval: IUIUrval | null;
  anvandsFor: string[];
}

const getInitialAnalys = (
  kodbehorighetIdFromURL: string | null,
  id: string | null,
  availableReports: ISelection[],
  orginalKodbehorighetsId: string | undefined
): ISelectedAnalys | null => {
  if (id === null) {
    return null;
  }

  const codePrivilege = availableReports.find(sel => {
    //Av någon anledning så innehåller inte availableReports impersonatedCodePrivilege id:et så den hittar inte rätt kodbehörighet.
    if (orginalKodbehorighetsId && sel.id === orginalKodbehorighetsId) {
      // Löser scenariot där man har valt en annan kodbehörighet i EkA och borrar på kostnad/intäkt så tar vi fram rätt kodbehörighet från availableReports så att vi sedan kan hitta rätt rapport på initialReport.
      return sel.id === orginalKodbehorighetsId;
    } else {
      return sel.id === kodbehorighetIdFromURL;
    }
  });

  const initialReport = codePrivilege
    ? codePrivilege.analysList.find(analys => analys.id === id)
    : null;

  if (!initialReport || !codePrivilege) {
    return null;
  } else {
    return {
      analys: {
        grundurval: {
          name: initialReport.baseSelection.name || '',
          id: initialReport.baseSelection.id || '',
        },
        name: initialReport.name,
        id: initialReport.id,
        idkod: initialReport.idkod,
        utdelningLopnr: initialReport.utdelatLopnr,
        categoryID: initialReport.kategori_ID,
        category: initialReport.kategori,
      },
      codePrivilege: {
        name: codePrivilege.name,
        id: codePrivilege.id,
      },
    };
  }
};

class GraphLinkConverter extends React.Component<IInnerProps, IState> {
  constructor(props: IInnerProps) {
    super(props);

    const query = new URLSearchParams(this.props.location.search);
    let kodbehorighetIdFromUrl = query.get('kid');
    let analysIdFromUrl = query.get('id');
    const sumsFromUrl = query.getAll('sums');
    const analysLevelParam = query.get('analysLevel');
    const urvalConditionsFromUrl: string[] = query.getAll(
      'urvalConditionsDrilling'
    );

    const urvalConditionsIUrvalUsedForDrilling: IUrvalUsedForDrilling[] =
      urvalConditionsFromUrl.reduce((accumulatedUrval, currUrval) => {
        if (!currUrval.includes('|')) {
          return accumulatedUrval;
        }

        const [attributeDisplayName, conditionValue, saknarVarde] =
          currUrval.split('|');

        if (
          attributeDisplayName == null ||
          conditionValue == null ||
          saknarVarde == null
        ) {
          return accumulatedUrval;
        }

        return accumulatedUrval.concat({
          attributeDisplayName: attributeDisplayName.trim(),
          conditionValue: conditionValue.trim(),
          missingValue: saknarVarde?.trim() === MISSING_VALUE_IN_KODDEL_URL,
        });
      }, [] as IUrvalUsedForDrilling[]);

    const analysLevel = getAnalysLevelFromUrlParam(analysLevelParam);

    // True om vi har borrat från detaljanalysen
    const drilledFromDetaljanalys =
      sumsFromUrl.length > 0 ||
      analysLevelParam !== null ||
      urvalConditionsIUrvalUsedForDrilling.length > 0;

    const locationState = this.props.history.location.state;

    let analysSettings: IFilledLocalAnalys | null = null;

    // This variable sets in EknomiskAnalysCard.tsx
    if (locationState) {
      const settings: IEkonomiskAnalysCardSettings =
        this.props.history.location.state.ekonomiskAnalysSettings;
      analysIdFromUrl = settings.analysId;
      kodbehorighetIdFromUrl = settings.kodbehorighetId;

      const params = this.parseParams(settings.params);

      analysSettings = {
        loading: false,
        name: params.name,
        category: params.kategori || undefined,
        title: params.rubrik || undefined,
        type: params.typ,
        dateInputType: getDateInputType(params.columns, props.standardColumns),
        isInterval: hasAnyInterval(params.columns, props.standardColumns),
        dates: settings.dates,
        columns: params.columns,
        sums: params.sums,
        urval: params.anvandarUrval,
        detaljUrval: params.detaljUrval ? params.detaljUrval : undefined,
        other: {
          budgetSummationMode: defaultBudgetSummationMode,
          includeEjBokfordaJournaler: false,
          denomination: params.standardBeloppBas ? params.standardBeloppBas : 1,
          decimals:
            params.standardBeloppAntalDecimaler != null
              ? params.standardBeloppAntalDecimaler
              : 2,
          showComparison: true,
          summationMode: defaultSummationMode,
          exkluderaPersonalKostnader: false,
          expandedSumIndex: 0,
        },
        defaultSettings: {
          budgetSummationMode: defaultBudgetSummationMode,
          decimals: 2,
          denomination: 1,
          includeEjBokfordaJournaler: false,
          showComparison: true,
          summationMode: defaultSummationMode,
          exkluderaPersonalKostnader: false,
          visaSummeringar: true,
        },
        expandToIndex: 0,
        anvandsFor: params.anvandsFor,
        sparatAnvandarUrval: null,
        lopsaldoDetaljrad: false,
        ai_prompt: null,
        detaljanalys: false,
        visaSummeringar: true,
      };
    }
    const analys = getInitialAnalys(
      kodbehorighetIdFromUrl,
      analysIdFromUrl,
      this.props.availableReports,
      props.selectedAnalys?.codePrivilege.id
    );

    if (
      kodbehorighetIdFromUrl !== null &&
      props.selectedAnalys?.codePrivilege.id !== kodbehorighetIdFromUrl
    ) {
      //Uppdatera impersonatedCodePrivilege i parent-komponenten EkonomiskAnalysPage
      const constructedImpersonatedCodePrivilegeFromURL: ISelectedAnalysCodePrivilege =
        {
          id: kodbehorighetIdFromUrl,
          name: 'ImpersonatedCodePrivilege sätts från URL',
        };
      this.props.updateImpersonatedCodePrivilege(
        constructedImpersonatedCodePrivilegeFromURL
      );
    }

    this.state = {
      originalAnalys: analys,
      originalSettings: analysSettings,
      sumsFromUrl: sumsFromUrl,
      analysLevel: analysLevel,
      urvalConditionsIUrvalUsedForDrilling:
        urvalConditionsIUrvalUsedForDrilling,
      drilledFromDetaljanalys: drilledFromDetaljanalys,
    };
  }

  componentDidUpdate(prevProps: IInnerProps, prevState: IState) {
    if (!equals(prevProps.selectedAnalys, this.props.selectedAnalys)) {
      this.setState({
        originalAnalys: null,
        originalSettings: null,
        analysLevel: AnalysLevelEnum.UNSET,
        sumsFromUrl: [],
        urvalConditionsIUrvalUsedForDrilling: [],
        drilledFromDetaljanalys: false,
      }); // Återställ state
    }
    // After any update, check if the parsed URL-analys requires closing the left menu
    this.checkAndCloseLeftMenu(prevProps, prevState);
  }

  componentDidMount() {
    // On mount check if we should close the left menu based on URL params
    this.checkAndCloseLeftMenu();
  }

  checkAndCloseLeftMenu(prevProps?: IInnerProps, prevState?: IState) {
    const shouldClose =
      (this.state.analysLevel !== AnalysLevelEnum.UNSET ||
        this.state.drilledFromDetaljanalys) &&
      this.state.originalAnalys?.analys.categoryID ===
        ANALYSKATEGORI_ID_VISA_SAMMANFATTNING;

    const prevShouldClose = prevState
      ? (prevState.analysLevel !== AnalysLevelEnum.UNSET ||
          prevState.drilledFromDetaljanalys) &&
        prevState.originalAnalys?.analys.categoryID ===
          ANALYSKATEGORI_ID_VISA_SAMMANFATTNING
      : false;

    if (shouldClose && !prevShouldClose && this.props.onCloseLeftMenu) {
      this.props.onCloseLeftMenu();
    }
  }

  parseParams(params: IParamValue[]): IParsedParams {
    let result: IParsedParams = {
      name: '',
      rubrik: null,
      kategori: null,
      standardBeloppBas: null,
      standardBeloppAntalDecimaler: null,
      typ: 'RAPPORTDIMENSIONLOPANDE',
      columns: [],
      sums: [],
      grundurvalId: '',
      kodbehorighetId: '',
      detaljUrval: null,
      anvandarUrval: null,
      anvandsFor: [],
    };

    params.forEach(param => {
      if (param.name === 'grundurval_id') {
        result.grundurvalId = param.value;
      } else if (param.name === 'kodbeh_id') {
        result.kodbehorighetId = param.value;
      } else if (param.name === 'analys') {
        const analys: IParsedAnalys = JSON.parse(param.value);
        result.name = analys.name;
        result.rubrik = analys.rubrik;
        result.kategori = analys.kategori;
        result.standardBeloppBas = parseInt(
          String(analys.standard_belopp_bas),
          10
        );
        result.standardBeloppAntalDecimaler =
          analys.standard_belopp_antal_decimaler;
        result.typ = analys.typ;
        result.columns = analys.columns.map((x, index) => ({
          name: x.CODE_CAPTION || '',
          id: index,
          data: x,
        }));
        result.sums = analys.sums;
        result.anvandsFor = analys.anvands_for ? analys.anvands_for : [];
      } else if (param.name === 'detaljurval_selection') {
        result.detaljUrval = convertTEDUrvalToUIUrval(
          UrvalConsts.EK_KONTO_URVAL_COLLECTION_NAME,
          JSON.parse(param.value),
          this.props.collectionMap
        ).unsafelyUnwrap();
      } else if (param.name === 'anvurval_selection') {
        result.anvandarUrval = convertTEDUrvalToUIUrval(
          UrvalConsts.EK_KONTO_URVAL_COLLECTION_NAME,
          JSON.parse(param.value),
          this.props.collectionMap
        ).unsafelyUnwrap();
      }
    });

    return result;
  }

  render() {
    return (
      <>
        {this.props.children({
          analysFromUrl: this.state.originalAnalys,
          settingsFromUrl: this.state.originalSettings,
          sumsFromUrl: this.state.sumsFromUrl,
          analysLevel: this.state.analysLevel,
          urvalConditionsIUrvalUsedForDrilling:
            this.state.urvalConditionsIUrvalUsedForDrilling,
          drilledFromDetaljanalys: this.state.drilledFromDetaljanalys,
        })}
      </>
    );
  }
}

const GraphLinkConverterWrapper: React.FC<IProps> = props => {
  const location = useLocation();
  const history = {
    location: {
      state: location.state as
        | { ekonomiskAnalysSettings: IEkonomiskAnalysCardSettings }
        | undefined,
    },
  } as History<{ ekonomiskAnalysSettings: IEkonomiskAnalysCardSettings }>;
  return (
    <GraphLinkConverter
      {...props}
      location={location as Location<unknown>}
      history={history}
    />
  );
};

export default GraphLinkConverterWrapper;
